import { Divider, LinearProgress, Stack, useTheme } from "@mui/material";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	ClientPortfolioDomainEnum,
	type ClientPropertyItemResponseDTO,
	type PortfolioListItemResponseDTO,
	type PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioListItemTitle } from "web_myplann/Portfolio/Components/PortfolioSections/PortfolioListItemTitle";
import { renderReportingThirdRow } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import { PropertyListItemTitle } from "web_myplann/Portfolio/Components/PropertySections/PropertyListItemTitle";
import { PortfolioListItemRedesign } from "web_myplann/Portfolio/Components/Redesign/PortfolioListItemRedesign";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import type { DashboardType } from "./DashboardTitleCard";

interface DashboardDetailContentProps {
	portfolios: PortfolioListItemResponseDTO[] | undefined;
	properties: ClientPropertyItemResponseDTO[] | undefined;
	reportingData: PortfolioListReportingSummaryResponseDTO[] | undefined;
	isLoading: boolean;
	dashboard: DashboardType;
}

export const DashboardDetailContent: FC<DashboardDetailContentProps> = ({
	portfolios,
	properties,
	reportingData,
	isLoading,
	dashboard,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const { palette } = theme;
	const navigate = useNavigate();
	const { hasRight: hasPortfolioRight } = usePortfolioRightOptional();

	const hasPortfolios = portfolios && portfolios.length > 0;
	const hasProperties = properties && properties.length > 0;
	const isDataLoaded = portfolios !== undefined && properties !== undefined;
	const isEmpty =
		!isLoading && isDataLoaded && !hasPortfolios && !hasProperties;

	return (
		<Stack pt={1.6} position="relative" flex={1}>
			{isLoading && (
				<LinearProgress
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: 2,
						zIndex: 1,
						backgroundColor: "transparent",
						"& .MuiLinearProgress-bar": {
							backgroundColor: dashboard.gradientTo,
						},
					}}
				/>
			)}
			<Stack
				flex={isEmpty ? 1 : undefined}
				sx={{ opacity: isLoading ? 0.6 : 1, transition: "opacity 0.3s ease" }}
			>
				{isEmpty && (
					<EmptyListSkeleton
						label={t(Resources.Dashboard.EmptyList.label)}
						description={t(Resources.Dashboard.EmptyList.description)}
						height="100%"
						flex={1}
						skeletonSize="lg"
					/>
				)}

				{hasPortfolios && (
					<Stack
						gap={1.6}
						mt={1.2}
						pb={1.6}
						divider={
							<Divider
								sx={{
									borderWidth: 0.75,
									borderColor: palette.deprecated.grey[100],
								}}
							/>
						}
					>
						{portfolios.map((item) =>
							hasPortfolioRight ? (
								<PortfolioListItemRedesign
									key={item.id}
									item={{ kind: PortfolioItemKind.Portfolio, data: item }}
									summaryData={reportingData}
								/>
							) : (
								<PortfolioListItemTitle
									key={item.id}
									data={item}
									summaryData={reportingData}
									customThirdRow={
										item.domain === ClientPortfolioDomainEnum.LifeInsurance
											? renderReportingThirdRow(item, reportingData, t)
											: undefined
									}
									onClick={() =>
										navigate(
											Routing.Portfolio.PortfolioDetail.path(
												item.id.toString(),
											),
										)
									}
								/>
							),
						)}
					</Stack>
				)}

				{hasPortfolios && hasProperties && (
					<Divider
						sx={{
							borderWidth: 0.75,
							borderColor: palette.deprecated.grey[100],
						}}
					/>
				)}

				{hasProperties && (
					<Stack
						gap={1.6}
						mt={1.2}
						pb={1.6}
						divider={
							<Divider
								sx={{
									borderWidth: 0.75,
									borderColor: palette.deprecated.grey[100],
								}}
							/>
						}
					>
						{properties.map((item) =>
							hasPortfolioRight ? (
								<PortfolioListItemRedesign
									key={item.id}
									item={{ kind: PortfolioItemKind.Property, data: item }}
									summaryData={reportingData}
								/>
							) : (
								<PropertyListItemTitle
									key={item.id}
									data={item}
									onClick={() =>
										navigate(
											Routing.Portfolio.PropertyDetail.path(item.id.toString()),
										)
									}
								/>
							),
						)}
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};
